'use client'

import { useRef, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Bell, Inbox, ArrowRight } from 'lucide-react'
import { NotificationMessageItem } from '@/components/NotificationMessageItem'
import { handleMessageAction } from './handlers/headerHandlers'
import type { NotificationAction, NotificationMessage } from '@/types/notification'
import type { SupabaseClient } from '@supabase/supabase-js'

interface ClientNotificationMenuProps {
  showNotificationMenu: boolean
  setShowNotificationMenu: (show: boolean) => void
  isNotificationMenuPinned: boolean
  setIsNotificationMenuPinned: (pinned: boolean) => void
  unreadCount: number
  recentMessages: NotificationMessage[]
  loadingMessages: boolean
  processingMessageId: string | number | null
  setProcessingMessageId: (id: string | number | null) => void
  setRecentMessages: React.Dispatch<React.SetStateAction<NotificationMessage[]>>
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>
  loadNotifications: () => Promise<void>
  supabase: SupabaseClient | null
}

export default function ClientNotificationMenu({
  showNotificationMenu,
  setShowNotificationMenu,
  isNotificationMenuPinned,
  setIsNotificationMenuPinned,
  unreadCount,
  recentMessages,
  loadingMessages,
  processingMessageId,
  setProcessingMessageId,
  setRecentMessages,
  setUnreadCount,
  loadNotifications,
  supabase,
}: ClientNotificationMenuProps) {
  const router = useRouter()
  const notificationMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!showNotificationMenu) return

    const handleClickOutside = (event: MouseEvent) => {
      if (notificationMenuRef.current && !notificationMenuRef.current.contains(event.target as Node)) {
        setShowNotificationMenu(false)
        setIsNotificationMenuPinned(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showNotificationMenu, setShowNotificationMenu, setIsNotificationMenuPinned])

  return (
    <div
      className="hidden md:block relative"
      ref={notificationMenuRef}
      onMouseEnter={() => {
        setShowNotificationMenu(true)
        if (recentMessages.length === 0 && !loadingMessages) loadNotifications()
      }}
      onMouseLeave={() => {
        if (!isNotificationMenuPinned) setShowNotificationMenu(false)
      }}
    >
      <Bell
        size={24}
        color="#4a4a4a"
        strokeWidth={1.5}
        className="cursor-pointer"
        onClick={() => {
          const next = !showNotificationMenu
          setShowNotificationMenu(next)
          setIsNotificationMenuPinned(next)
          if (next && recentMessages.length === 0 && !loadingMessages) loadNotifications()
        }}
      />

      {unreadCount > 0 && (
        <div className="absolute top-0 right-0 w-[10px] h-[10px] bg-palette-blue-50 rounded-full border-2 border-white" />
      )}

      {showNotificationMenu && (
        <div
          className="absolute right-0 top-full mt-1 w-96 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50"
          onMouseEnter={() => setShowNotificationMenu(true)}
          onMouseLeave={() => {
            if (!isNotificationMenuPinned) setShowNotificationMenu(false)
          }}
        >
          {/* 헤더 */}
          <div className="px-5 py-4 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 w-full">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                  <Bell className="w-5 h-5 text-white" />
                </div>

                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-gray-900">알림</h3>
                  {unreadCount > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5">{unreadCount}개의 읽지 않은 알림</p>
                  )}
                </div>

                {unreadCount > 0 && (
                  <span className="ml-auto px-2.5 py-1 text-xs font-bold bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full shadow-sm">
                    {unreadCount}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* 리스트 */}
          {recentMessages.length > 0 ? (
            <div className="max-h-[400px] overflow-y-auto">
              {recentMessages.map((message) => {
                const onMessageAction = async (action: NotificationAction) => {
                  if (!supabase) return
                  await handleMessageAction(
                    message,
                    action,
                    supabase,
                    setRecentMessages,
                    setUnreadCount,
                    setProcessingMessageId,
                    loadNotifications,
                  )
                }

                return (
                  <NotificationMessageItem
                    key={message.id}
                    message={message}
                    processingMessageId={processingMessageId}
                    onAction={onMessageAction}
                    scope="client"
                  />
                )
              })}
            </div>
          ) : (
            <div className="px-5 py-16 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
                <Inbox className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-sm font-semibold text-gray-900 mb-1.5">새로운 견적서가 없습니다</p>
              <p className="text-xs text-gray-500 leading-relaxed">
                프리랜서가 견적서를 제출하면
                <br />
                여기에 표시됩니다
              </p>
            </div>
          )}

          {/* CTA */}
          <div className="px-5 py-4 bg-gradient-to-b from-white to-gray-50/50 border-t border-gray-100">
            <button
              onClick={() => {
                router.push('/client/inbox')
                setShowNotificationMenu(false)
                setIsNotificationMenuPinned(false)
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 rounded-xl transition-all border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5"
            >
              <Inbox className="w-4 h-4" />
              모든 메시지 보기
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

